import {
  useSalesServiceSalesOrdersModificationPriceCreate,
  useSalesServiceSalesOrdersModifyCreate,
  useSalesServiceSalesOrdersPartialUpdate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  ContractorType,
  EditAccidents,
  OrderInspectionStatus,
  PriceAccidents,
  Status9e7Enum
} from '@insurance/api/requests'
import { RefundMoneyButton } from '@insurance/components'
import { useAppNavigate, useModifyPayment } from '@insurance/hooks'
import { paths } from '@insurance/routes'
import { ProductCode } from '@insurance/types'
import { DEFAULT_CURRENCY } from '@shared/constants'
import { replaceEmptyWithUndefined, showApiErrors } from '@shared/utils'
import { Button, Form } from 'antd'
import { useTranslation } from 'react-i18next'

import { useAccidentInsuranceValues } from './AccidentInsuranceContext'
import { AccidentFormValues } from './type'
import {
  formProps,
  normalizeCommonData,
  normalizeEditOrderData,
  normalizeModifyOrderData,
  removeAccidentValuesFromStorage
} from './utils'
import { InsurancePaymentControls } from '../InsurancePaymentControls'

type Props = {
  steps: number
  onReadyToSaveData?: () => void
}

export const AccidentInsuranceControls = ({
  steps,
  onReadyToSaveData
}: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const navigate = useAppNavigate()
  const { stepKey, priceKey, orderKey, contractorProps } = formProps()

  const step = Form.useWatch(stepKey.name, { preserve: true })
  const price = Form.useWatch(priceKey.name, { preserve: true })
  const contractor = Form.useWatch(contractorProps.name, { preserve: true })
  const { isEditable, order } = useAccidentInsuranceValues()

  const isLegalEntity = contractor?.content_type === ContractorType.LEGALENTITY

  const orderPrice = {
    amount: order?.full_paid_amount ? Number(order.full_paid_amount) : 0
  }

  const { payPermission, onFinishPay } = useModifyPayment({ formInstance })

  const canPay =
    !isEditable || (payPermission && price?.amount > orderPrice.amount)
  const canRefund = isEditable && orderPrice.amount > price?.amount
  const canModify = isEditable && orderPrice.amount === price?.amount

  const { mutate: calculate, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate({
      onError: (error) => showApiErrors(error, formInstance)
    })

  const { mutate: calculateModify, isPending: isCalculatingModifyLoading } =
    useSalesServiceSalesOrdersModificationPriceCreate({
      onError: (error) => showApiErrors(error, formInstance)
    })

  const {
    mutate: calculateModifyRefund,
    isPending: isCalculatingModifyRefundLoading
  } = useSalesServiceSalesOrdersModificationPriceCreate({
    onSuccess: (data) => {
      formInstance.setFieldValue(priceKey.name, {
        currency: DEFAULT_CURRENCY,
        amount: Number(Number(data?.price_data?.amount)?.toFixed(2))
      })
    },
    onError: (error) => showApiErrors(error, formInstance)
  })

  const { mutate: initModifyOrder, isPending: isOnlyModifyLoading } =
    useSalesServiceSalesOrdersModifyCreate({
      onError: (error) => showApiErrors(error, formInstance)
    })

  const { mutate: updateOrder, isPending: isUpdateOrderLoading } =
    useSalesServiceSalesOrdersPartialUpdate({
      onError: (error) => showApiErrors(error, formInstance)
    })

  const runCalculate = async (checkPersons: boolean, onSuccess: () => void) => {
    const values = formInstance.getFieldsValue(true) as AccidentFormValues
    const bodyValues = await normalizeCommonData(values, checkPersons)

    calculate(
      { code: ProductCode.ACCIDENTS, requestBody: bodyValues },
      {
        onSuccess: (data) => {
          const draftOrderId = (data as PriceAccidents)?.order?.id

          formInstance.setFieldValue(orderKey.name, draftOrderId)

          if (draftOrderId) runUpdate(draftOrderId, onSuccess)
        }
      }
    )
  }

  const runUpdate = async (draftOrderId: number, onSuccess: () => void) => {
    const values = formInstance.getFieldsValue(true) as AccidentFormValues
    const bodyValues = await normalizeCommonData(values, false)

    updateOrder(
      {
        id: draftOrderId,
        requestBody: {
          stage: step + 1,
          status: Status9e7Enum.DATA_ENTRY,
          accidents: bodyValues as EditAccidents
        }
      },
      {
        onSuccess: (data) => {
          formInstance.setFieldValue(priceKey.name, {
            currency: DEFAULT_CURRENCY,
            amount: Number(Number(data?.amount)?.toFixed(2))
          })
          window.scrollTo({ top: 0, behavior: 'smooth' })
          onSuccess()
        }
      }
    )
  }

  const runStep = async (checkPersons: boolean, onSuccess: () => void) => {
    await formInstance.validateFields()

    if (order?.id) {
      const values = formInstance.getFieldsValue(true) as AccidentFormValues
      const bodyValues = await normalizeModifyOrderData(values)

      calculateModify({ id: order.id, requestBody: bodyValues }, { onSuccess })
      return
    }

    const values = formInstance.getFieldsValue(true) as AccidentFormValues
    const draftOrderId = values.order_id

    if (draftOrderId) await runUpdate(draftOrderId, onSuccess)
    else await runCalculate(checkPersons, onSuccess)
  }

  const handleCalculate = (onSuccess: () => void) => runStep(false, onSuccess)
  const handleGoToCustomer = (onSuccess: () => void) => runStep(true, onSuccess)

  const handleCalculateRefund = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true) as AccidentFormValues
    const bodyValues = await normalizeModifyOrderData(values)

    if (order?.id)
      calculateModifyRefund({ requestBody: bodyValues, id: order.id })
  }

  const handleOnlyModify = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true) as AccidentFormValues

    if (!order?.id) return

    const bodyValues = await normalizeModifyOrderData(values)

    initModifyOrder(
      {
        id: order.id,
        requestBody: replaceEmptyWithUndefined({
          ...bodyValues,
          status: OrderInspectionStatus.OFFER_READY
        })
      },
      {
        onSuccess: (modifiedOrder) =>
          onFinishPay(modifiedOrder.id, () => {
            navigate(
              modifiedOrder.id
                ? paths.orders.details(modifiedOrder.id)
                : paths.salesPanel.root
            )
          })
      }
    )
  }

  return (
    <InsurancePaymentControls
      steps={steps}
      onReadyToSaveData={onReadyToSaveData}
      removeFromStorage={() => removeAccidentValuesFromStorage(order?.id)}
      keepDraftOrderOnPrevStep
      handleCalculate={handleCalculate}
      isCalculatingLoading={
        isCalculatingLoading ||
        isCalculatingModifyLoading ||
        isUpdateOrderLoading
      }
      isLegalEntity={isLegalEntity}
      disabledPay={!canPay}
      errorUnwrapKeys={['accidents']}
      middleStep={{
        onClick: handleGoToCustomer,
        isLoading:
          isCalculatingLoading ||
          isCalculatingModifyLoading ||
          isUpdateOrderLoading
      }}
      normalizeOrderData={async (values) => ({
        id: values[orderKey.name] as number,
        requestBody: {
          accidents: await normalizeEditOrderData(values as AccidentFormValues)
        }
      })}
      modify={
        isEditable
          ? {
              order,
              normalizeOrderData: async (values) => ({
                id: order!.id!,
                requestBody: await normalizeModifyOrderData(
                  values as AccidentFormValues
                )
              })
            }
          : undefined
      }
      extraActions={
        <>
          {canModify && (
            <Button
              type="primary"
              loading={isOnlyModifyLoading}
              onClick={handleOnlyModify}
            >
              {t('modify_insurance_menu')}
            </Button>
          )}

          {canRefund && (
            <RefundMoneyButton
              loading={isCalculatingModifyRefundLoading}
              onChangeRefund={handleCalculateRefund}
              onRefund={handleOnlyModify}
            />
          )}
        </>
      }
    />
  )
}
